<template>
    <div class="reference-bottom-sheet" :style="props.positionStyle" ref="bottomSheetRef">
        <p class="title h4">{{ t('trend_report_evaluation_modal_title') }}</p>
        <ul class="evaluation-reason-list body-3--regular dot-style-list">
            <li
                v-for="(item, index) in evaluationDescList"
                :key="index"
                v-dompurify-html="computedEvaluationDesc(item, index)"
            ></li>
        </ul>
        <!--<p class="content body-3--regular">{{ props.referenceInfo.content }}</p> -->
        <div class="evaluation-desc-wrap">
            <p class="body-3">{{ t('trend_report_evaluation_modal_sub_title_1') }}</p>
            <img
                src="../../../public/assets/images/evaluation-chart.png"
                :alt="t('trend_report_evaluation_modal_chart_image_alt')"
            />
            <p class="body-3--regular padding-bottom">
                {{ t('trend_report_evaluation_modal_body_1_text_1') }}
            </p>
            <p class="body-3--regular">
                {{ t('trend_report_evaluation_modal_body_1_text_2') }}
            </p>
        </div>
        <div class="evaluation-desc-wrap">
            <p class="body-3">{{ t('trend_report_evaluation_modal_sub_title_2') }}</p>
            <p class="body-3--regular">
                {{ t('trend_report_evaluation_modal_body_2_text') }}
            </p>
        </div>
        <button class="close-btn h6" @click="handleCloseBtnClick">닫기</button>
    </div>
</template>

<script setup lang="ts">
import type { Evaluation } from '@common/openapi-client/openapi';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const emit = defineEmits(['close']);

const props = defineProps<{
    evaluationDescInfo: Evaluation | null;
    positionStyle: string | Record<string, string>;
}>();

const evaluationDescList = ref<string[]>([
    t('trend_report_evaluation_modal_desc_list_1'),
    // t('trend_report_evaluation_modal_desc_list_2'),
]);

const bottomSheetRef = ref<HTMLElement | null>(null);

const handleCloseBtnClick = () => {
    emit('close');
};

const computedEvaluationDesc = (item: string, index: number) => {
    let changeText = item;

    if (index === 0) {
        const evalText = props.evaluationDescInfo?.EvaluationText;
        const evalColor = props.evaluationDescInfo?.EvaluationColor;

        // #EvaluationText# → span 태그로 치환
        const evalStyledTag = `<span style="color: ${evalColor}">${evalText}</span>`;
        changeText = changeText.replace(/#EvaluationText#/g, evalStyledTag);

        const trendType = props.evaluationDescInfo?.EvaluationReason?.BodyCompTrendType;

        // 타입 단언을 통해 entries의 타입 명시
        (Object.entries(trendType ?? {}) as [string, { Label: string; Color: string }][]).forEach(([key, value]) => {
            const labelTag = `#${key}_LABEL#`;
            const styledTag = `<span style="color: ${value.Color}">${value.Label}</span>`;
            changeText = changeText.replace(new RegExp(labelTag, 'g'), styledTag);
        });
    } else {
        // BodyType은 그대로 치환
        changeText = changeText.replace(/#BodyType#/g, props.evaluationDescInfo?.EvaluationReason?.BodyType ?? '');

        // SameTrendRatio는 <b> 태그로 강조
        const ratio = props.evaluationDescInfo?.EvaluationReason?.SameTrendRatio;
        changeText = changeText.replace(/#SameTrendRatio#/g, `<b>${ratio}</b>`);
    }

    return changeText;
};

let justOpened = false;

const handleClickOutside = (e: MouseEvent) => {
    if (justOpened) {
        justOpened = false;
        return; // 첫 클릭은 무시
    }

    const el = bottomSheetRef.value;
    if (
        el &&
        !el.contains(e.target as Node) &&
        !(e.target as HTMLElement).classList.contains('evaluation-title-box') &&
        !(e.target as HTMLElement).classList.contains('evaluation-title-text')
    ) {
        emit('close');
    }
};

onMounted(() => {
    justOpened = true; // 마운트 되자마자 한 번은 무시
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
.reference-bottom-sheet {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 3rem 1.5rem;
    box-shadow: 0px -20px 20px 0px #0000000d;
    background-color: $primary-white;
    border-radius: 2.5rem 2.5rem 0 0;
    z-index: 100;
    max-height: 60%;
    overflow-y: auto;
}

.title {
    padding-bottom: 0.5rem;
    color: $grey-7;
}

.close-btn {
    background: $grey-2;
    border-radius: 1.5rem;
    padding: 1.3rem 0;
    width: 45%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    margin-top: 2rem;
    color: $grey-7;
}

.evaluation-reason-list {
    color: $grey-7;
}

.evaluation-desc-wrap {
    color: $grey-7;
    padding-top: 2rem;

    > p {
        white-space: pre-line;
        word-break: keep-all;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        margin: 1rem 0;
    }

    .padding-bottom {
        padding-bottom: 3rem;
    }
}

.body-3--regular {
    line-height: 170%;
}
</style>
